import React, { useCallback, useEffect, useState } from 'react';
import { getKbSyncSchedule, saveKbSyncSchedule, runKbSyncNow } from '../../lib/api/kb-api';

// KB targets 5종(전체 고정). REAL/SUPPLY 는 targets 미사용.
const ALL_TARGET_KEYS = ['SALE', 'JEONSE', 'SALE_IDX', 'JEONSE_IDX', 'BUYER_SE'];
const DEFAULT_CRON = '0 21 * * 1'; // 매주 월 21:00

// 구분별 안내
const CATEGORY_META = {
  KB: { label: 'KB', note: 'KB 지수·증감·매수심리(전체 5종)를 동기화합니다.' },
  REAL: { label: '실거래', note: '전체 랭커대상 단지 · 최근 7년 · 시세+실거래를 동기화합니다. (워커 고정 동작)' },
  SUPPLY: { label: '공급', note: '공급 데이터 동기화. (실행 워커 준비중 — 스케줄만 등록 가능)' },
};

// 요일: cron dow(0=일 ~ 6=토). 사용자에겐 cron 을 숨기고 요일/시간으로만 노출.
const WEEKDAYS = [
  { v: 1, l: '월' }, { v: 2, l: '화' }, { v: 3, l: '수' }, { v: 4, l: '목' },
  { v: 5, l: '금' }, { v: 6, l: '토' }, { v: 0, l: '일' },
];
const pad2 = (n) => String(n).padStart(2, '0');
const cronToWeekly = (cron) => {
  const parts = String(cron || '').trim().split(/\s+/);
  if (parts.length !== 5) return { days: [1], time: '21:00' };
  const mm = Number(parts[0]) || 0;
  const hh = Number(parts[1]) || 0;
  const dow = parts[4];
  let days;
  if (dow === '*' || dow === '') days = [0, 1, 2, 3, 4, 5, 6];
  else days = dow.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n)).map((n) => (n === 7 ? 0 : n));
  return { days, time: `${pad2(hh)}:${pad2(mm)}` };
};
const weeklyToCron = (days, time) => {
  const [hh, mm] = String(time || '21:00').split(':').map((x) => Number(x) || 0);
  const uniq = Array.from(new Set((days || []).map((n) => (n === 7 ? 0 : n)))).sort((a, b) => a - b);
  const dowField = (uniq.length === 0 || uniq.length === 7) ? '*' : uniq.join(',');
  return `${mm} ${hh} * * ${dowField}`;
};
const weeklyLabel = (days, time) => {
  if (!days || days.length === 0) return '요일 선택';
  if (days.length === 7) return `매일 ${time}`;
  const order = [1, 2, 3, 4, 5, 6, 0];
  const label = order.filter((d) => days.includes(d)).map((d) => WEEKDAYS.find((w) => w.v === d)?.l).join(',');
  return `매주 ${label} ${time}`;
};

// 구분별 동기화 스케줄 패널 (KB / REAL / SUPPLY 공용)
function SyncSchedulePanel({ category = 'KB' }) {
  const meta = CATEGORY_META[category] || CATEGORY_META.KB;
  const [weekDays, setWeekDays] = useState([1]);
  const [weekTime, setWeekTime] = useState('21:00');
  const [isEnabled, setIsEnabled] = useState(true);
  const [schedMeta, setSchedMeta] = useState({ nextRunAt: null, lastRunAt: null, lastStatus: null });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');

  const flash = (text) => { setMsg(text); setTimeout(() => setMsg(''), 2600); };

  const loadAll = useCallback(async () => {
    try {
      const s = await getKbSyncSchedule(category).catch(() => null);
      if (s) {
        const w = cronToWeekly(s.cronExpr || DEFAULT_CRON);
        setWeekDays(w.days);
        setWeekTime(w.time);
        setIsEnabled(s.isEnabled !== false);
        setSchedMeta({ nextRunAt: s.nextRunAt || null, lastRunAt: s.lastRunAt || null, lastStatus: s.lastStatus || null });
      }
    } catch { /* noop */ }
  }, [category]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleDay = (v) => setWeekDays((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const handleSaveSchedule = async () => {
    if (!weekDays || weekDays.length === 0) { flash('요일을 선택하세요.'); return; }
    setSaving(true);
    try {
      await saveKbSyncSchedule({
        category,
        scheduleName: `${meta.label} 동기화`,
        targets: ALL_TARGET_KEYS,
        scheduleType: 'CRON',
        cronExpr: weeklyToCron(weekDays, weekTime),
        timezone: 'Asia/Seoul',
        isEnabled,
      });
      await loadAll();
      flash('스케줄이 저장되었습니다.');
    } catch (e) { flash('스케줄 저장 실패'); } finally { setSaving(false); }
  };

  const handleRunNow = async () => {
    if (!window.confirm(`지금 ${meta.label} 데이터를 즉시 동기화할까요? (워커가 곧 실행)`)) return;
    setRunning(true);
    try {
      await runKbSyncNow(category);
      flash('즉시 동기화를 예약했습니다. 워커가 곧 실행합니다.');
    } catch (e) { flash('즉시 동기화 요청 실패'); } finally { setRunning(false); }
  };

  return (
    <div className="crawling-management">
      {msg && <p className="crawling-note-warn" style={{ color: '#059669' }}>{msg}</p>}

      <section className="crawling-section">
        <h4>{meta.label} 동기화 스케줄</h4>
        <p className="crawling-note" style={{ color: '#64748b', fontSize: 12, margin: '0 0 10px' }}>{meta.note}</p>

        <div className="crawling-field">
          <label className="crawling-label">요일 · 시간</label>
          <div className="crawling-weekdays">
            {WEEKDAYS.map((d) => (
              <button
                type="button"
                key={d.v}
                className={`crawling-day ${weekDays.includes(d.v) ? 'on' : ''}`}
                onClick={() => toggleDay(d.v)}
              >
                {d.l}
              </button>
            ))}
            <input className="crawling-day-time" type="time" value={weekTime} onChange={(e) => setWeekTime(e.target.value)} />
          </div>
          <div className="crawling-weekday-presets">
            <button type="button" onClick={() => setWeekDays([1, 2, 3, 4, 5])}>평일</button>
            <button type="button" onClick={() => setWeekDays([0, 6])}>주말</button>
            <button type="button" onClick={() => setWeekDays([0, 1, 2, 3, 4, 5, 6])}>매일</button>
            <button type="button" onClick={() => setWeekDays([1])}>매주 월</button>
            <span className="crawling-hint">{weeklyLabel(weekDays, weekTime)}</span>
          </div>
        </div>

        <div className="crawling-field" style={{ marginTop: 8 }}>
          <label className="crawling-check">
            <input type="checkbox" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
            <span>스케줄 사용</span>
          </label>
        </div>

        <div
          style={{
            marginTop: 12, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 20px',
            fontSize: 16, fontWeight: 700, color: '#334155',
          }}
        >
          <span>다음 실행: <b style={{ fontSize: 18, fontWeight: 800, color: '#2563eb' }}>{schedMeta.nextRunAt || '-'}</b></span>
          {schedMeta.lastRunAt && (
            <span>
              마지막 실행: <b style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{schedMeta.lastRunAt}</b>
              {schedMeta.lastStatus && (
                <span style={{ marginLeft: 6, fontSize: 14, fontWeight: 800, color: schedMeta.lastStatus === 'success' ? '#059669' : '#dc2626' }}>
                  ({schedMeta.lastStatus})
                </span>
              )}
            </span>
          )}
        </div>

        <div className="crawling-actions">
          <button className="crawling-btn" onClick={handleRunNow} disabled={running}>
            {running ? '요청 중...' : '⚡ 즉시 동기화'}
          </button>
          <button className="crawling-btn primary" onClick={handleSaveSchedule} disabled={saving}>
            {saving ? '저장 중...' : '스케줄 저장'}
          </button>
        </div>
      </section>

      <p className="crawling-note" style={{ color: '#94a3b8', fontSize: 12 }}>
        ※ 실제 동기화는 워커가 스케줄대로 수행합니다. 즉시 동기화는 워커 다음 폴링(≤10초)에 실행됩니다.
      </p>
    </div>
  );
}

export default SyncSchedulePanel;
